import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import cx from 'classnames';
import LANGUAGE from 'components/AppTranslationWrapper/constants/language';
import { DatePickerToolbar } from './components/DatePickerToolbar';
import calendarIcon from './icons/calendar-icon.svg';

const INITIAL_PROPS = {
  views: ['year', 'month'],
  variant: 'inline',
  mask: '____/__',
  autoOk: true,
  inputVariant: 'outlined',
  emptyLabel: 'YYYY/MM',
  format: 'YYYY/MMMM',
  orientation: 'portrait',
};

const nonSelectedStyles = {
  fontWeight: 400,
  fontSize: 16,
  color: 'black',
};

const useStyles = makeStyles((theme) => ({
  root: {
    boxSizing: 'border-box',
    width: '100%',
    border: '1px solid #FF8C69',
    background: '#fff4f1',
  },
  calendar: ({ isLessYearsThenContainerHeight }) => ({
    '& .MuiPickersYearSelection-container': {
      height: isLessYearsThenContainerHeight ? 'auto' : 250,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      padding: '0 0 0 30px',
    },
    '& .MuiPickersMonthSelection-container': {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    '& .MuiPickersYear-root': {
      margin: '1px 0 0 0',
      flex: '0 1 25%',
      justifyContent: 'flex-start',
    },
    '& .MuiPickersBasePicker-pickerView': {
      minHeight: isLessYearsThenContainerHeight ? 'unset' : 250,
      justifyContent: 'flex-start',
      [theme.breakpoints.down('sm')]: {
        maxWidth: 'unset',
      },
    },
  }),
  nonSelected: {
    '& .MuiPickersYear-yearSelected': nonSelectedStyles,
    '& .MuiPickersMonthSelection-container': {
      '& .MuiPickersMonth-monthSelected': nonSelectedStyles,
    },
  },
  input: {
    maxHeight: 50,
    fontWeight: 400,
    fontSize: 16,
    color: 'black',
  },
  notchedOutline: {
    border: 'none',
  },
  paper: {
    maxWidth: 450,
    [theme.breakpoints.down('xs')]: {
      // width: 'calc(100% - 40px)',
      maxWidth: 'unset',
      width: '100%',
      left: '0px !important',
    },
  },
}));

const localeByLanguages = {
  [LANGUAGE.en.SHORT]: 'en',
  [LANGUAGE.he.SHORT]: 'he',
  [LANGUAGE.fr.SHORT]: 'fr',
  [LANGUAGE.es.SHORT]: 'es',
  [LANGUAGE.tr.SHORT]: 'tr',
};

export const TjDatePicker = ({
  initialValue,
  minDate,
  maxDate,
  onChange,
  onClose,
  initialFocusedDate,
  isOpenOnFocus = false,
}) => {
  const datesDifference = moment(maxDate).year() - moment(minDate).year();
  const isLessYearsThenContainerHeight = datesDifference < 23;
  const [value, setValue] = useState(initialValue);
  const [isOpen, setIsOpen] = useState(false);
  const currentLanguage = useSelector((state) => state.main.lang);
  const classes = useStyles({ isLessYearsThenContainerHeight });
  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    (async () => {
      if (currentLanguage !== LANGUAGE.en.SHORT) {
        await import(`moment/locale/${localeByLanguages[currentLanguage]}`);
      }
      moment.locale(localeByLanguages[currentLanguage]);
    })();
  }, [currentLanguage]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={localeByLanguages[currentLanguage]}>
      <KeyboardDatePicker
        {...INITIAL_PROPS}
        open={isOpen}
        onLoad={isOpenOnFocus ? handleOpen : null}
        value={value}
        minDate={minDate}
        maxDate={maxDate}
        onChange={onChange}
        onClose={handleClose}
        onMonthChange={setValue}
        initialFocusedDate={initialFocusedDate}
        emptyLabel={INITIAL_PROPS.emptyLabel}
        classes={{
          root: classes.root,
        }}
        ToolbarComponent={
          isLessYearsThenContainerHeight
            ? () => null
            // eslint-disable-next-line react/no-unstable-nested-components
            : () => (
              <DatePickerToolbar
                minDate={minDate}
                maxDate={maxDate}
                initialFocusedDate={initialFocusedDate}
              />
            )
        }
        keyboardIcon={<img src={calendarIcon} alt="calendar-icon" />}
        InputAdornmentProps={{
          onClick: handleOpen,
        }}
        InputProps={{
          onClick: handleOpen,
          readOnly: true,
          placeholder: INITIAL_PROPS.emptyLabel,
          classes: {
            root: classes.input,
            notchedOutline: classes.notchedOutline,
          },
        }}
        PopoverProps={{
          transitionDuration: 0,
          PaperProps: {
            classes: {
              root: classes.paper,
            },
          },
          classes: {
            root: cx(classes.calendar, { [classes.nonSelected]: !value }),
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      />
    </MuiPickersUtilsProvider>
  );
};

TjDatePicker.propTypes = {
  initialValue: PropTypes.object,
  minDate: PropTypes.object.isRequired,
  maxDate: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  initialFocusedDate: PropTypes.object.isRequired,
  isOpenOnFocus: PropTypes.bool,
};
